import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
      return await res.json();
    } catch (err) { return {}; }
  },
  post: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (err) { return { error: err.message }; }
  }
};

export default function LeadNurturingPage({ token }) {
  const { language } = useLanguage();
  const [funnelStats, setFunnelStats] = useState(null);
  const [pendingMessages, setPendingMessages] = useState([]);
  const [upgradeTargets, setUpgradeTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('funnel');
  const [sendingDay, setSendingDay] = useState(null);

  useEffect(() => {
    if (token) loadData();
    else setLoading(false);
  }, [token]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [funnel, pending, targets] = await Promise.all([
        api.get('/api/lead-nurturing/funnel-stats', token),
        api.get('/api/lead-nurturing/pending-messages', token),
        api.get('/api/lead-nurturing/upgrade-targets', token),
      ]);
      if (funnel.success) setFunnelStats(funnel);
      if (pending.success) setPendingMessages(pending.messages || []);
      if (targets.success) setUpgradeTargets(targets.targets || []);
    } catch (err) { console.error('Lead nurturing load error:', err); }
    setLoading(false);
  };

  const markSent = async (leadId, day) => {
    setSendingDay(`${leadId}-${day}`);
    await api.post(`/api/lead-nurturing/mark-sent/${leadId}/${day}`, {}, token);
    await loadData();
    setSendingDay(null);
  };

  const funnelStages = [
    { key: 'new', label: 'New', labelHi: 'नया', color: 'bg-blue-500', icon: 'fa-user-plus' },
    { key: 'contacted', label: 'Contacted', labelHi: 'संपर्क किया', color: 'bg-cyan-500', icon: 'fa-phone' },
    { key: 'nurturing', label: 'Nurturing', labelHi: 'Nurturing', color: 'bg-purple-500', icon: 'fa-seedling' },
    { key: 'demo_scheduled', label: 'Demo Set', labelHi: 'Demo Set', color: 'bg-amber-500', icon: 'fa-calendar' },
    { key: 'demo_done', label: 'Demo Done', labelHi: 'Demo Done', color: 'bg-orange-500', icon: 'fa-check-circle' },
    { key: 'negotiating', label: 'Negotiating', labelHi: 'बातचीत', color: 'bg-yellow-500', icon: 'fa-handshake' },
    { key: 'closed_won', label: 'Won', labelHi: 'जीता', color: 'bg-green-500', icon: 'fa-trophy' },
    { key: 'closed_lost', label: 'Lost', labelHi: 'हारा', color: 'bg-red-500', icon: 'fa-times-circle' },
  ];

  const tabs = [
    { id: 'funnel', label: language === 'hi' ? 'Lead Funnel' : 'Lead Funnel', icon: 'fa-filter' },
    { id: 'drip', label: language === 'hi' ? 'Drip Campaign' : 'Drip Campaign', icon: 'fa-paper-plane' },
    { id: 'upgrade', label: language === 'hi' ? 'Upgrade Targets' : 'Upgrade Targets', icon: 'fa-arrow-up' },
  ];

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500"></div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="lead-nurturing-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-800" style={{fontFamily: 'Outfit, sans-serif'}}>
          {language === 'hi' ? 'Lead Nurturing / लीड Nurturing' : 'Lead Nurturing'}
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          {language === 'hi' ? '31-Day WhatsApp Drip Campaign और Lead Funnel Management' : '31-Day WhatsApp Drip Campaign & Lead Funnel Management'}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${
              activeTab === tab.id ? 'bg-white shadow text-orange-700' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <i className={`fa-solid ${tab.icon}`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Funnel Tab */}
      {activeTab === 'funnel' && funnelStats && (
        <div className="space-y-6">
          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <p className="text-xs text-gray-500 uppercase tracking-wider">{language === 'hi' ? 'कुल Leads' : 'Total Leads'}</p>
              <p className="text-3xl font-bold text-gray-800 mt-1">{funnelStats.total_leads}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <p className="text-xs text-gray-500 uppercase tracking-wider">{language === 'hi' ? 'Conversion Rate' : 'Conversion Rate'}</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{funnelStats.conversion_rate}%</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <p className="text-xs text-gray-500 uppercase tracking-wider">{language === 'hi' ? 'Won' : 'Won'}</p>
              <p className="text-3xl font-bold text-green-500 mt-1">{funnelStats.stats?.closed_won || 0}</p>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border">
              <p className="text-xs text-gray-500 uppercase tracking-wider">{language === 'hi' ? 'Active Nurturing' : 'Active Nurturing'}</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">{funnelStats.stats?.nurturing || 0}</p>
            </div>
          </div>

          {/* Visual Funnel */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border">
            <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <i className="fa-solid fa-filter text-orange-500"></i>
              {language === 'hi' ? 'Sales Funnel' : 'Sales Funnel'}
            </h3>
            <div className="space-y-2">
              {funnelStages.map((stage, i) => {
                const count = funnelStats.stats?.[stage.key] || 0;
                const maxCount = Math.max(...Object.values(funnelStats.stats || {}), 1);
                const width = Math.max((count / maxCount) * 100, 5);
                return (
                  <div key={stage.key} className="flex items-center gap-3" data-testid={`funnel-stage-${stage.key}`}>
                    <div className="w-28 text-right text-sm text-gray-600 flex items-center justify-end gap-2">
                      <i className={`fa-solid ${stage.icon} text-xs`}></i>
                      {language === 'hi' ? stage.labelHi : stage.label}
                    </div>
                    <div className="flex-1 bg-gray-100 rounded-full h-8 overflow-hidden">
                      <div className={`${stage.color} h-full rounded-full flex items-center justify-end pr-3 transition-all duration-500`} style={{width: `${width}%`}}>
                        <span className="text-white text-xs font-bold">{count}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Drip Campaign Tab */}
      {activeTab === 'drip' && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl p-6 shadow-sm border">
            <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <i className="fa-solid fa-paper-plane text-blue-500"></i>
              {language === 'hi' ? 'Pending WhatsApp Messages' : 'Pending WhatsApp Messages'}
              <span className="bg-orange-100 text-orange-700 text-xs px-2 py-0.5 rounded-full">{pendingMessages.length}</span>
            </h3>

            {pendingMessages.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <i className="fa-solid fa-check-circle text-4xl text-green-300 mb-3 block"></i>
                <p className="font-medium">{language === 'hi' ? 'कोई pending message नहीं!' : 'No pending messages!'}</p>
                <p className="text-sm mt-1">{language === 'hi' ? 'सभी drip messages भेज दिए गए हैं' : 'All drip messages have been sent'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingMessages.map((msg, i) => (
                  <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border hover:bg-gray-100 transition" data-testid={`pending-msg-${i}`}>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                        <i className="fa-brands fa-whatsapp text-green-600 text-lg"></i>
                      </div>
                      <div>
                        <p className="font-medium text-sm text-gray-700">{msg.lead_phone} — Day {msg.day}</p>
                        <p className="text-xs text-gray-500">{msg.feature}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => markSent(msg.lead_phone, msg.day)}
                      disabled={sendingDay === `${msg.lead_phone}-${msg.day}`}
                      className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 disabled:opacity-50"
                    >
                      {sendingDay === `${msg.lead_phone}-${msg.day}` ? '...' : language === 'hi' ? 'भेजा गया' : 'Mark Sent'}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Campaign Info */}
          <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl p-6 text-white">
            <h3 className="font-bold text-lg mb-3" style={{fontFamily: 'Outfit, sans-serif'}}>
              {language === 'hi' ? '31-Day AI Feature Drip Campaign' : '31-Day AI Feature Drip Campaign'}
            </h3>
            <p className="text-white/70 text-sm mb-4">
              {language === 'hi'
                ? 'हर दिन एक नई AI feature का WhatsApp message automatically भेजा जाता है। 31 दिनों में सभी features cover हो जाते हैं।'
                : 'One new AI feature WhatsApp message is sent daily. All 31 features are covered in 31 days.'}
            </p>
            <div className="flex flex-wrap gap-2">
              {['MCQ Generator', 'Notice Simplifier', 'Fee Reminder', 'Attendance Alerts', 'Question Paper', 'Progress Summary'].map((f, i) => (
                <span key={i} className="px-3 py-1 bg-white/10 rounded-full text-xs border border-white/20">{f}</span>
              ))}
              <span className="px-3 py-1 bg-orange-500/30 rounded-full text-xs border border-orange-400/30">+25 more</span>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Targets Tab */}
      {activeTab === 'upgrade' && (
        <div className="bg-white rounded-2xl p-6 shadow-sm border">
          <h3 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <i className="fa-solid fa-arrow-up text-emerald-500"></i>
            {language === 'hi' ? 'Upgrade Targets' : 'Upgrade Targets'}
            <span className="bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">{upgradeTargets.length}</span>
          </h3>

          {upgradeTargets.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <i className="fa-solid fa-arrow-trend-up text-4xl text-gray-300 mb-3 block"></i>
              <p className="font-medium">{language === 'hi' ? 'कोई upgrade target नहीं' : 'No upgrade targets'}</p>
              <p className="text-sm mt-1">{language === 'hi' ? 'जब Basic/Standard plan के customers आएंगे तो यहाँ दिखेंगे' : 'Basic/Standard plan customers will appear here'}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {upgradeTargets.map((target, i) => (
                <div key={i} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border">
                  <div>
                    <p className="font-medium text-sm">{target.name || target.school_name}</p>
                    <p className="text-xs text-gray-500">{target.phone} — Plan: {target.plan || 'basic'}</p>
                  </div>
                  <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                    {language === 'hi' ? 'Upgrade करें' : 'Upgrade'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
